import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PropsWithChildren, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { BrainCreationProvider } from "@/lib/components/AddKnowledgeBaseModal/brainCreation-provider";
import { Menu } from "@/lib/components/Menu/Menu";
import { useOutsideClickListener } from "@/lib/components/Menu/hooks/useOutsideClickListener";
import { OrganisationOnboardingModal } from "@/lib/components/OrganisationOnboardingModal";
import LazyBackgroundVideo from "@/lib/components/ui/LazyBackgroundVideo/LazyBackgroundVideo";
import { KnowledgeToFeedProvider } from "@/lib/context";
import { ChatsProvider } from "@/lib/context/ChatsProvider";
import { useKnowledgeBaseContext } from "@/lib/context/KnowledgeBaseProvider/hooks/useKnowledgeBaseContext";
import { KnowledgeBaseProvider } from "@/lib/context/KnowledgeBaseProvider/knowledge-base-provider";
import { MenuProvider } from "@/lib/context/MenuProvider/Menu-provider";
import { useMenuContext } from "@/lib/context/MenuProvider/hooks/useMenuContext";
import { NotificationsProvider } from "@/lib/context/NotificationsProvider/notifications-provider";
import { OrganisationProvider } from "@/lib/context/OrganisationProvider";
import { PageHeaderProvider } from "@/lib/context/PageHeaderProvider";
import { UpdateMetadata } from "@/lib/helpers/updateMetadata";
import { useAuth } from "@/lib/hooks";

import styles from "./App.module.scss";

const AppContent = ({ children }: PropsWithChildren): JSX.Element => {
  const { fetchAllKnowledgeBases } = useKnowledgeBaseContext();
  const { onClickOutside } = useOutsideClickListener();
  const { isAuthenticated, isLoading } = useAuth();
  const { isOpened } = useMenuContext();
  const { pathname } = useLocation();

  const isHomePage = pathname === "/";

  useEffect(() => {
    if (isAuthenticated) {
      void fetchAllKnowledgeBases();
    }
  }, [isAuthenticated]);

  return (
    <div className="flex flex-1 flex-col overflow-auto">
      <OrganisationOnboardingModal />
      <div
        className={`${styles.app_container} ${
          isHomePage ? styles.homepage_container : ""
        }`}
      >
        {isAuthenticated && !isLoading && (
          <>
            {isHomePage && (
              <>
                <LazyBackgroundVideo videoSrc="/agents-bg.mp4" opacity={1} />
                <div className={styles.homepage_overlay} />
              </>
            )}
            <Menu />
          </>
        )}

        <div
          onClick={onClickOutside}
          className={`${styles.content_container} ${
            isOpened ? styles.blured : ""
          }`}
        >
          {children}
        </div>
        <UpdateMetadata />
      </div>
    </div>
  );
};

const queryClient = new QueryClient();

const App = ({ children }: PropsWithChildren): JSX.Element => {
  return (
    <QueryClientProvider client={queryClient}>
      <OrganisationProvider>
        <KnowledgeBaseProvider>
          <KnowledgeToFeedProvider>
            <BrainCreationProvider>
              <NotificationsProvider>
                <MenuProvider>
                  <ChatsProvider>
                    <PageHeaderProvider>
                      <AppContent>{children}</AppContent>
                    </PageHeaderProvider>
                  </ChatsProvider>
                </MenuProvider>
              </NotificationsProvider>
            </BrainCreationProvider>
          </KnowledgeToFeedProvider>
        </KnowledgeBaseProvider>
      </OrganisationProvider>
    </QueryClientProvider>
  );
};

export { App };
